/**
 * Wischgesten (24.10.2026) — die Regeln, ohne DOM, damit sie prüfbar sind.
 *
 * Eine Web-App vom Homescreen hat auf iOS kein Zurück-Wischen; der Rand ist
 * frei. Deshalb gilt **der Ort, an dem der Finger anfängt**:
 *
 * | Start            | Seite                   | Unteransicht        |
 * |------------------|-------------------------|---------------------|
 * | rechter Rand     | nächster Unterreiter    | nichts              |
 * | linker Rand      | vorheriger Unterreiter  | zurück              |
 * | Zeile mit Geste  | ihre Aktion             | ihre Aktion         |
 * | Seite mit Tagen  | Tag/Zeitraum            | Tag/Zeitraum        |
 * | sonst Inhalt     | Unterreiter             | nichts              |
 *
 * „Unterreiter“ sind die Reiter oben auf einer Seite (Tag · Woche ·
 * Bibliothek). Die Leiste unten wird nie gewischt (Timo, 24.10.2026) — der
 * Rand überspringt das Tagesblättern, bleibt aber auf der Seite. Die eine
 * Ausnahme (Räume, 27.10.2026): **Hochwischen auf dem Raum-Knopf** öffnet
 * das Raum-Blatt — nur auf dem Knopf, nie über die ganze Leiste und nie vom
 * unteren Bildschirmrand, dort liegt die Home-Geste von iOS (`swipeUpOpens`).
 *
 * Jede Geste ist eine Abkürzung für einen Knopf, der bleibt — sichtbar, mit
 * Tastatur und VoiceOver bedienbar (Apple HIG: nie der einzige Weg).
 */

/** Randzone in px — schmal, sonst kollidiert sie mit dem Inhalt. */
export const EDGE_PX = 24;
/** Erst ab dieser Strecke entscheidet sich, ob gewischt oder gescrollt wird. */
export const LOCK_PX = 10;
/** Ziehen zum Aktualisieren löst ab dieser Strecke (nach Dämpfung) aus. */
export const PULL_TRIGGER_PX = 64;

export type StartZone = 'edge-left' | 'edge-right' | 'content';

export function startZone(x: number, width: number): StartZone {
  if (x <= EDGE_PX) return 'edge-left';
  if (x >= width - EDGE_PX) return 'edge-right';
  return 'content';
}

/**
 * Waagerecht oder senkrecht? `null`, solange es zu früh ist. Waagerecht nur,
 * wenn deutlich waagerecht — schräges Scrollen bleibt Scrollen.
 */
export function lockDirection(dx: number, dy: number): 'h' | 'v' | null {
  if (Math.hypot(dx, dy) < LOCK_PX) return null;
  return Math.abs(dx) > Math.abs(dy) * 1.2 ? 'h' : 'v';
}

/** Genug gewischt? Weit genug — oder kurz und schnell. */
export function commits(dx: number, width: number, ms: number): boolean {
  const distance = Math.abs(dx);
  if (distance >= Math.min(width * 0.25, 120)) return true;
  return distance >= 40 && distance / Math.max(ms, 1) >= 0.4;
}

export type Direction = 1 | -1;

export type SwipeIntent =
  | { kind: 'tab'; dir: Direction }
  | { kind: 'back' }
  | { kind: 'page'; dir: Direction }
  | { kind: 'row'; side: 'left' | 'right' };

export interface SwipeContextInfo {
  zone: StartZone;
  /** Nach rechts gewischt (Finger bewegt sich nach rechts)? */
  towardRight: boolean;
  /** Eine Unteransicht hat „zurück“ angemeldet. */
  inSubview: boolean;
  /** Die Seite hat Unterreiter angemeldet. */
  hasTabs: boolean;
  /** Der Finger liegt auf einem Bereich, der Tage/Zeiträume blättert. */
  inPager: boolean;
  /** Welche Richtungen die Zeile unter dem Finger kann. */
  row: { left: boolean; right: boolean } | null;
  /** Der Finger liegt in etwas, das selbst waagerecht scrollt. */
  inScroller: boolean;
}

export function intentFor(info: SwipeContextInfo): SwipeIntent | null {
  // Nach rechts wischen heißt: zurück bzw. das Vorherige.
  const dir: Direction = info.towardRight ? -1 : 1;
  const tab = (to: Direction): SwipeIntent | null =>
    info.hasTabs && !info.inSubview ? { kind: 'tab', dir: to } : null;
  if (info.zone === 'edge-right') return info.towardRight ? null : tab(1);
  if (info.zone === 'edge-left') {
    if (!info.towardRight) return null;
    return info.inSubview ? { kind: 'back' } : tab(-1);
  }
  if (info.inScroller) return null;
  if (info.row) {
    // Nach rechts wischen zeigt die linke Seite der Zeile frei — und umgekehrt.
    if (info.towardRight && info.row.right) return { kind: 'row', side: 'right' };
    if (!info.towardRight && info.row.left) return { kind: 'row', side: 'left' };
  }
  if (info.inPager) return { kind: 'page', dir };
  return tab(dir);
}

/** Nächster bzw. vorheriger Unterreiter — am Ende bleibt es stehen. */
export function neighbourTab<T>(tabs: readonly T[], current: T, dir: Direction): T | null {
  const index = tabs.indexOf(current);
  if (index < 0) return null;
  return tabs[index + dir] ?? null;
}

/** Ziehen dämpfen, damit der Inhalt nicht am Finger klebt. */
export function damp(distance: number): number {
  return distance <= 0 ? 0 : Math.min(distance * 0.5, 120);
}

/** Unterer Bildschirmrand: gehört der Home-Geste von iOS, nie uns. */
export const BOTTOM_EDGE_PX = 24;
/** So weit nach oben, bis das Raum-Blatt aufgeht. */
export const SWIPE_UP_PX = 32;

/**
 * Öffnet das Hochwischen auf dem Raum-Knopf das Blatt? Nur, wenn der Finger
 * über dem unteren Rand anfing, deutlich senkrecht nach oben ging und weit
 * genug — schräg oder seitlich bleibt es ein Tippen bzw. nichts.
 */
export function swipeUpOpens(
  startY: number,
  dx: number,
  dy: number,
  viewportHeight: number,
): boolean {
  if (startY >= viewportHeight - BOTTOM_EDGE_PX) return false;
  if (lockDirection(dx, dy) !== 'v') return false;
  return dy <= -SWIPE_UP_PX;
}
